"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Minimap = void 0;
class Minimap {
    constructor(parent) {
        this.size = 200;
        this.mapReady = false;
        this.expanded = false;
        this.map = null;
        this.mapMinX = 0;
        this.mapMinY = 0;
        this.mapWidth = 0;
        this.mapHeight = 0;
        this.scale = 1;
        this.offsetX = 0;
        this.offsetY = 0;
        this.onClick = (event) => {
            event.stopPropagation();
            this.setExpanded(!this.expanded);
        };
        this.onKeyDown = (event) => {
            if (this.expanded && event.key === 'Escape') {
                this.setExpanded(false);
            }
        };
        this.container = document.createElement('div');
        this.container.id = 'minimap-container';
        this.container.style.cssText = `
      position: absolute; top: 38px; right: 12px; z-index: 997;
      width: ${this.size}px; height: ${this.size}px;
      border: 1px solid #444; border-radius: 6px;
      overflow: visible; pointer-events: auto; cursor: zoom-in;
    `;
        this.mapCanvas = document.createElement('canvas');
        this.mapCanvas.width = this.size;
        this.mapCanvas.height = this.size;
        this.mapCtx = this.mapCanvas.getContext('2d');
        this.dotCanvas = document.createElement('canvas');
        this.dotCanvas.width = this.size;
        this.dotCanvas.height = this.size;
        this.dotCanvas.style.cssText = 'width:100%;height:100%;';
        this.dotCtx = this.dotCanvas.getContext('2d');
        this.container.appendChild(this.dotCanvas);
        this.hint = document.createElement('div');
        this.hint.textContent = 'Click to expand';
        this.hint.style.cssText = `
      position:absolute; right:6px; bottom:5px; padding:2px 5px;
      border-radius:3px; background:rgba(0,0,0,0.65); color:#ccc;
      font:10px sans-serif; pointer-events:none;
    `;
        this.container.appendChild(this.hint);
        this.legend = document.createElement('div');
        this.legend.id = 'replay-legend';
        this.legend.style.cssText = `
      position:absolute; top:calc(100% + 8px); right:0; width:${this.size}px;
      display:grid; gap:4px; pointer-events:auto;
    `;
        this.container.appendChild(this.legend);
        this.container.addEventListener('click', this.onClick);
        document.addEventListener('keydown', this.onKeyDown);
        parent.appendChild(this.container);
    }
    updateMap(bsp) {
        this.map = bsp;
        this.mapReady = false;
        this.mapCtx.clearRect(0, 0, this.size, this.size);
        if (!bsp.models || !bsp.models.length || !bsp.models[0].faces.length)
            return;
        const model = bsp.models[0];
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;
        let minZ = Infinity, maxZ = -Infinity;
        for (const face of model.faces) {
            const buf = face.buffer;
            for (let i = 0; i < buf.length; i += 7) {
                const x = buf[i];
                const y = buf[i + 1];
                const z = buf[i + 2];
                if (x < minX)
                    minX = x;
                if (x > maxX)
                    maxX = x;
                if (y < minY)
                    minY = y;
                if (y > maxY)
                    maxY = y;
                if (z < minZ)
                    minZ = z;
                if (z > maxZ)
                    maxZ = z;
            }
        }
        if (minX === maxX || minY === maxY)
            return;
        const padding = 20;
        this.mapMinX = minX;
        this.mapMinY = minY;
        this.mapWidth = maxX - minX;
        this.mapHeight = maxY - minY;
        const availW = this.size - padding * 2;
        const availH = this.size - padding * 2;
        const scaleX = availW / this.mapWidth;
        const scaleZ = availH / this.mapHeight;
        this.scale = Math.min(scaleX, scaleZ);
        this.offsetX = padding + (availW - this.mapWidth * this.scale) / 2;
        this.offsetY = padding + (availH - this.mapHeight * this.scale) / 2;
        const ctx = this.mapCtx;
        ctx.fillStyle = '#111';
        ctx.fillRect(0, 0, this.size, this.size);
        const heightRange = Math.max(1, maxZ - minZ);
        const lightX = 0.35;
        const lightY = -0.45;
        const lightZ = 0.82;
        for (const face of model.faces) {
            const buf = face.buffer;
            for (let i = 0; i + 20 < buf.length; i += 21) {
                const ax = buf[i];
                const ay = buf[i + 1];
                const az = buf[i + 2];
                const bx = buf[i + 7];
                const by = buf[i + 8];
                const bz = buf[i + 9];
                const cx = buf[i + 14];
                const cy = buf[i + 15];
                const cz = buf[i + 16];
                const abx = bx - ax;
                const aby = by - ay;
                const abz = bz - az;
                const acx = cx - ax;
                const acy = cy - ay;
                const acz = cz - az;
                const nx = aby * acz - abz * acy;
                const ny = abz * acx - abx * acz;
                const nz = abx * acy - aby * acx;
                const length = Math.hypot(nx, ny, nz) || 1;
                const normalX = nx / length;
                const normalY = ny / length;
                const normalZ = nz / length;
                const directional = Math.max(0, normalX * lightX + normalY * lightY + normalZ * lightZ);
                const horizontal = Math.abs(normalZ);
                const averageZ = (az + bz + cz) / 3;
                const height = (averageZ - minZ) / heightRange;
                const brightness = Math.min(1, 0.18 + horizontal * 0.48 + directional * 0.26 + height * 0.12);
                ctx.beginPath();
                ctx.moveTo(this.projectX(ax), this.projectY(ay));
                ctx.lineTo(this.projectX(bx), this.projectY(by));
                ctx.lineTo(this.projectX(cx), this.projectY(cy));
                ctx.closePath();
                ctx.fillStyle = `rgb(${Math.round(12 + brightness * 34)},${Math.round(32 + brightness * 92)},${Math.round(20 + brightness * 52)})`;
                ctx.fill();
                ctx.strokeStyle = `rgba(8,20,12,${0.18 + (1 - brightness) * 0.28})`;
                ctx.lineWidth = 0.35;
                ctx.stroke();
            }
        }
        this.mapReady = true;
    }
    setExpanded(expanded) {
        this.expanded = expanded;
        this.legend.style.display = expanded ? 'none' : 'grid';
        const viewportSize = Math.max(200, Math.min(window.innerWidth - 24, window.innerHeight - 120));
        this.size = expanded ? viewportSize : 200;
        this.container.style.cssText = expanded ? `
      position:fixed; top:12px; left:50%; right:auto; transform:translateX(-50%);
      z-index:25; width:${this.size}px; height:${this.size}px;
      border:1px solid #777; border-radius:8px; overflow:visible;
      background:#111; pointer-events:auto; cursor:zoom-out;
    ` : `
      position:absolute; top:38px; right:12px; z-index:997;
      width:${this.size}px; height:${this.size}px;
      border:1px solid #444; border-radius:6px; overflow:visible;
      pointer-events:auto; cursor:zoom-in;
    `;
        this.hint.textContent = expanded ? 'Click or Esc to close' : 'Click to expand';
        this.mapCanvas.width = this.size;
        this.mapCanvas.height = this.size;
        this.dotCanvas.width = this.size;
        this.dotCanvas.height = this.size;
        this.dotCanvas.style.cssText = 'width:100%;height:100%;';
        this.mapCtx = this.mapCanvas.getContext('2d');
        this.dotCtx = this.dotCanvas.getContext('2d');
        if (this.map) {
            this.updateMap(this.map);
        }
    }
    toggleExpanded() {
        this.setExpanded(!this.expanded);
    }
    projectX(x) {
        return this.offsetX + (x - this.mapMinX) * this.scale;
    }
    projectY(y) {
        return this.offsetY + (y - this.mapMinY) * this.scale;
    }
    drawPlayer(x, y, replayTrails, currentFrames, highlightedReplay = null) {
        const ctx = this.dotCtx;
        ctx.clearRect(0, 0, this.size, this.size);
        if (!this.mapReady)
            return;
        ctx.drawImage(this.mapCanvas, 0, 0);
        if (replayTrails && currentFrames) {
            replayTrails.drawOnMinimap(ctx, (x) => this.projectX(x), (y) => this.projectY(y), currentFrames, highlightedReplay);
        }
        if (highlightedReplay)
            return;
        const px = this.projectX(x);
        const py = this.projectY(y);
        ctx.beginPath();
        ctx.arc(px, py, 3, 0, Math.PI * 2);
        ctx.fillStyle = '#e94560';
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1;
        ctx.stroke();
    }
    show() { this.container.style.display = ''; }
    hide() { this.container.style.display = 'none'; }
}
exports.Minimap = Minimap;
